if (!customElements.get('product-form')) {
  const cartState = {
    promise: null,

    load(forceRefresh = false) {
      if (forceRefresh) this.promise = null;

      if (!this.promise) {
        const root =
          window.Shopify && window.Shopify.routes
            ? window.Shopify.routes.root
            : '/';

        this.promise = fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        })
          .then((response) => {
            if (!response.ok) throw new Error('Unable to read cart');
            return response.json();
          })
          .catch((error) => {
            this.promise = null;
            throw error;
          });
      }

      return this.promise;
    },

    clear() {
      this.promise = null;
    },
  };

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput = this.form.querySelector('[name=id]');
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.alreadyInCartMessage = 'This item is in your cart';
        this.checkoutButtonLabel = 'Checkout';
        this.submitButtonLabel =
          this.submitButton.querySelector(':scope > span') ||
          this.submitButton.querySelector('span');
        this.defaultSubmitButtonLabel = this.submitButtonLabel
          ? this.submitButtonLabel.textContent.trim()
          : '';
        this.isAlreadyInCart = false;
        this.cartCheckToken = 0;

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.submitButton.addEventListener('click', (event) => {
          if (!this.isAlreadyInCart) return;

          event.preventDefault();
          event.stopPropagation();
          this.openCartDrawer();
        });

        this.submitButton.addEventListener('keydown', (event) => {
          if (!this.isAlreadyInCart) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;

          event.preventDefault();
          this.openCartDrawer();
        });

        this.variantIdInput.addEventListener('change', () => {
          this.setAlreadyInCart(false);
          this.syncAlreadyInCartState(true);
        });

        this.variantObserver = new MutationObserver(() => {
          this.setAlreadyInCart(false);
          this.syncAlreadyInCartState(true);
        });

        this.variantObserver.observe(this.variantIdInput, {
          attributes: true,
          attributeFilter: ['value'],
        });

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          this.unsubscribeCartUpdate = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            () => {
              cartState.clear();
              this.syncAlreadyInCartState(true);
            }
          );
        }

        this.syncAlreadyInCartState();
      }

      disconnectedCallback() {
        if (this.variantObserver) this.variantObserver.disconnect();

        if (typeof this.unsubscribeCartUpdate === 'function') {
          this.unsubscribeCartUpdate();
        }
      }

      get selectedVariantId() {
        return String(this.variantIdInput.value || '');
      }

      setAlreadyInCart(alreadyInCart) {
        this.isAlreadyInCart = alreadyInCart;

        this.submitButton.toggleAttribute(
          'data-already-in-cart',
          alreadyInCart
        );

        if (alreadyInCart) {
          this.handleErrorMessage(this.alreadyInCartMessage);
          this.submitButton.removeAttribute('aria-disabled');
          this.setAlreadyInCartButtonAppearance(true);
          return;
        }

        this.handleErrorMessage();
        this.setAlreadyInCartButtonAppearance(false);

        if (
          !this.submitButton.classList.contains('loading') &&
          !this.submitButton.disabled
        ) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      setAlreadyInCartButtonAppearance(alreadyInCart = false) {
        this.submitButton.dataset.editorialBuyActive = 'false';

        [
          'background',
          'background-color',
          'background-image',
          'color',
          'opacity',
          'cursor',
        ].forEach((property) => {
          this.submitButton.style.removeProperty(property);
        });

        const label =
          this.submitButtonLabel ||
          this.submitButton.querySelector(':scope > span') ||
          this.submitButton.querySelector('span');

        if (label) {
          label.style.removeProperty('color');
          label.textContent = alreadyInCart
            ? this.checkoutButtonLabel
            : this.defaultSubmitButtonLabel;
        }

        if (alreadyInCart) {
          this.submitButton.dataset.checkoutDrawerTrigger = 'true';
          this.submitButton.setAttribute('aria-label', 'Open cart drawer');
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }

        delete this.submitButton.dataset.checkoutDrawerTrigger;
        this.submitButton.removeAttribute('aria-label');
      }

      ensurePurchaseStripErrorPlacement() {
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        const purchaseStrip = this.closest('.product-purchase-strip');
        if (!purchaseStrip || !purchaseStrip.parentNode) return;

        this.errorMessageWrapper.classList.add(
          'product-form__error-message-wrapper--purchase-strip',
          'product-form__error-message-wrapper--below-purchase-strip'
        );

        if (purchaseStrip.nextElementSibling !== this.errorMessageWrapper) {
          purchaseStrip.parentNode.insertBefore(
            this.errorMessageWrapper,
            purchaseStrip.nextSibling
          );
        }
      }

      openCartDrawer() {
        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer) {
          if (cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }

          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open(this.submitButton);
            return true;
          }
        }

        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.open === 'function') {
          cartNotification.open();
          return true;
        }

        const cartTrigger =
          document.getElementById('cart-icon-bubble') ||
          document.querySelector('[data-cart-drawer-trigger]') ||
          document.querySelector('a[href$="/cart"]') ||
          document.querySelector('a[href*="/cart"]');

        if (cartTrigger && typeof cartTrigger.click === 'function') {
          cartTrigger.click();
          return true;
        }

        if (window.routes && window.routes.cart_url) {
          window.location = window.routes.cart_url;
        }

        return false;
      }

      emphasizeAlreadyInCartMessage() {
        this.openCartDrawer();

        const wrapper = this.errorMessageWrapper;
        if (!wrapper) return;

        wrapper.classList.remove('is-emphasized');

        window.requestAnimationFrame(() => {
          wrapper.classList.add('is-emphasized');
        });

        if (this.alreadyInCartEmphasisTimeout) {
          window.clearTimeout(this.alreadyInCartEmphasisTimeout);
        }

        this.alreadyInCartEmphasisTimeout = window.setTimeout(() => {
          wrapper.classList.remove('is-emphasized');
        }, 620);
      }

      async syncAlreadyInCartState(forceRefresh = false) {
        const variantId = this.selectedVariantId;
        const checkToken = ++this.cartCheckToken;

        if (!variantId) {
          this.setAlreadyInCart(false);
          return false;
        }

        try {
          const cart = await cartState.load(forceRefresh);

          if (
            checkToken !== this.cartCheckToken ||
            variantId !== this.selectedVariantId
          ) {
            return this.isAlreadyInCart;
          }

          const alreadyInCart = cart.items.some(
            (item) => String(item.variant_id) === variantId
          );

          this.setAlreadyInCart(alreadyInCart);
          return alreadyInCart;
        } catch (error) {
          console.error(error);
          return false;
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.isAlreadyInCart) {
          this.openCartDrawer();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.submitButton.setAttribute('aria-disabled', 'true');

        const alreadyInCart = await this.syncAlreadyInCartState(true);
        if (alreadyInCart) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove(
          'hidden'
        );

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              cartState.clear();

              const itemIsNowInCart =
                await this.syncAlreadyInCartState(true);

              if (itemIsNowInCart) {
                this.error = false;
                this.openCartDrawer();
                return;
              }

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (!soldOutMessage) return;

              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            cartState.clear();
            this.setAlreadyInCart(true);

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error && !this.isAlreadyInCart) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            this.querySelector('.loading-overlay__spinner').classList.add(
              'hidden'
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.ensurePurchaseStripErrorPlacement();

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        const purchaseStrip = this.closest('.product-purchase-strip');
        const isAlreadyInCartMessage = Boolean(
          errorMessage &&
            String(errorMessage).toLowerCase().includes('in your cart')
        );

        if (purchaseStrip) {
          purchaseStrip.classList.toggle(
            'has-product-cart-message',
            isAlreadyInCartMessage
          );
        }

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        } else {
          this.errorMessageWrapper.classList.remove('is-emphasized');
        }
      }
    }
  );
}